"use client";

import React, { useState, useEffect, useCallback } from "react";
import {
  TextInput,
  ActionIcon,
  Box,
  Text,
  Group,
  Image,
  Loader,
  Tooltip,
  UnstyledButton,
  Box as MantineBox,
  Input as MantineInput,
  Text as MantineText,
} from "@mantine/core";
import { IconX, IconSearch, IconArrowRight } from "@tabler/icons-react";
import { useImageUpload } from "@/hooks/useImageUpload";
import { useImageSearchStore } from "@/stores/imageSearchStore";
import { useFiltersStore } from "@/stores/filtersStore";
import { useGlobalData } from "@/context/globalContext";
import { ImageCropOverlay } from "@/components/ImageCropOverlay";
import type { PixelCrop } from "react-image-crop";
import styles from "./styles.module.scss";

const iconTooltipProps = {
  openDelay: 350,
  position: "bottom" as const,
  withArrow: true,
  events: { hover: true, focus: true, touch: false },
};

interface ImageUploadZoneProps {
  onSearchComplete?: () => void;
  onClose?: () => void;
  label?: string;
}

export function ImageUploadZone({
  onSearchComplete,
  onClose,
  label = "IMAGE SEARCH",
}: ImageUploadZoneProps) {
  const loading = useImageSearchStore((state) => state.loading);
  const sourceItem = useImageSearchStore((state) => state.sourceItem);
  const storeSearchText = useImageSearchStore((state) => state.searchText);
  const recropRequested = useImageSearchStore((state) => state.recropRequested);
  const cropSourceUrl = useImageSearchStore((state) => state.cropSourceUrl);
  const lastCropArea = useImageSearchStore((state) => state.lastCropArea);
  const { searchWithText, startSearch, clearRecropRequest, saveCropSource } = useImageSearchStore(
    (state) => state.actions
  );
  const activeFilters = useFiltersStore((state) => state.activeFilters);
  const { exchangeRates } = useGlobalData();
  const isImageSearchActive = !!sourceItem;

  const [textInput, setTextInput] = useState(storeSearchText);
  // URL currently shown in crop overlay (null = crop UI closed)
  const [cropImage, setCropImage] = useState<string | null>(null);

  const handleTextSearch = () => {
    searchWithText(
      textInput.trim(),
      activeFilters,
      exchangeRates as Record<string, number> | undefined
    );
  };

  const {
    previewUrl,
    originalImageUrl,
    pasteLink,
    isDragging,
    setPasteLink,
    fileInputRef,
    handleFileChange,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    openImageMode,
    closeImageMode,
    triggerFileInput,
  } = useImageUpload({
    onSearchComplete,
    onClose,
  });

  // Activate paste listener for clipboard images as soon as the zone is visible
  useEffect(() => {
    openImageMode();
  }, [openImageMode]);

  // When a new image is loaded locally, open the crop UI immediately
  useEffect(() => {
    if (previewUrl && !isImageSearchActive) {
      setCropImage(originalImageUrl || previewUrl);
    }
  }, [previewUrl, originalImageUrl, isImageSearchActive]);

  // When the banner thumbnail is clicked, open crop with the original source image
  useEffect(() => {
    if (recropRequested && cropSourceUrl) {
      setCropImage(cropSourceUrl);
      clearRecropRequest();
    }
  }, [recropRequested, cropSourceUrl, clearRecropRequest]);

  const handleCropConfirm = useCallback(
    (croppedBase64: string, croppedPixels: PixelCrop) => {
      // Persist original image + crop selection so re-crop can restore them
      if (cropImage) saveCropSource(cropImage, croppedPixels);
      const fakeItem = { imageurl: croppedBase64 } as SearchResultItem;
      startSearch(
        fakeItem,
        activeFilters,
        undefined,
        exchangeRates as Record<string, number> | undefined
      );
      setCropImage(null);
      onSearchComplete?.();
    },
    [cropImage, saveCropSource, startSearch, activeFilters, exchangeRates, onSearchComplete]
  );

  const handleCropCancel = useCallback(() => {
    setCropImage(null);
    // If there was no previous search active, close the whole zone
    if (!isImageSearchActive) {
      closeImageMode();
    }
  }, [isImageSearchActive, closeImageMode]);

  const fileInput = (
    <MantineInput
      ref={fileInputRef}
      type="file"
      accept="image/*"
      style={{ display: "none" }}
      onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
    />
  );

  if (cropImage) {
    return (
      <MantineBox className={styles.container} data-crop-active>
        {fileInput}
        <ImageCropOverlay
          imageUrl={cropImage}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
          initialCropArea={
            cropImage === cropSourceUrl
              ? ((lastCropArea as PixelCrop | null) ?? undefined)
              : undefined
          }
        />
      </MantineBox>
    );
  }

  return (
    <MantineBox className={styles.container} data-dragging={isDragging || undefined}>
      <Group justify="space-between" align="center" mb={6} data-close>
        <MantineText span className={styles.label}>
          {label}
        </MantineText>
        <Tooltip label="Close image search" {...iconTooltipProps}>
          <ActionIcon
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation();
              closeImageMode();
            }}
            aria-label="Close image search"
            size="sm"
            type="button"
            w={24}
            h={24}
          >
            <IconX size={24} color="black" />
          </ActionIcon>
        </Tooltip>
      </Group>
      {fileInput}
      <Box
        className={`${styles.imageDropZone} ${isDragging ? styles.dragging : ""} ${previewUrl ? styles.hasPreview : ""}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isImageSearchActive && sourceItem ? (
          <Group gap="sm" align="center" w="100%" wrap="nowrap">
            <Image
              src={sourceItem.imageurl}
              alt="Preview"
              w={40}
              h={40}
              fit="cover"
              radius={0}
              style={{ cursor: "pointer", flexShrink: 0 }}
              onClick={() => setCropImage(cropSourceUrl || sourceItem.imageurl || null)}
              title="Click to re-crop"
            />
            {loading ? (
              <Loader size="xs" color="#1500ff" />
            ) : (
              <TextInput
                value={textInput}
                onChange={(e) => setTextInput(e.currentTarget.value)}
                onKeyDown={(e) => e.key === "Enter" && handleTextSearch()}
                onFocus={(e) => e.currentTarget.select()}
                placeholder="ADD TO YOUR SEARCH"
                classNames={{ input: styles.textRefineInput }}
                flex={1}
                rightSection={
                  textInput.trim() ? (
                    <Tooltip label="Add text to refine results" {...iconTooltipProps}>
                      <ActionIcon
                        variant="transparent"
                        onClick={handleTextSearch}
                        aria-label="Search"
                        w={24}
                        h={24}
                      >
                        <IconArrowRight size={24} color="black" />
                      </ActionIcon>
                    </Tooltip>
                  ) : null
                }
              />
            )}
          </Group>
        ) : (
          <Box className={styles.dropInner}>
            <Text className={styles.title}>
              <MantineText span>DROP, PASTE, OR </MantineText>
              <br />
              <UnstyledButton
                type="button"
                onClick={triggerFileInput}
                className={styles.uploadLink}
              >
                ADD AN IMAGE
              </UnstyledButton>
            </Text>

            <Group gap={8} align="center" w="100%">
              <Box flex={1} h={1} bg="#ccc" />
              <Text size="xs" c="#666">
                OR
              </Text>
              <Box flex={1} h={1} bg="#ccc" />
            </Group>

            <TextInput
              value={pasteLink}
              onChange={(e) => setPasteLink(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && pasteLink.trim()) setCropImage(pasteLink.trim());
              }}
              placeholder="PASTE IMAGE LINK"
              classNames={{ input: styles.linkInputField }}
              w="100%"
              rightSection={
                <Tooltip label="Search by image link" {...iconTooltipProps}>
                  <ActionIcon
                    variant="transparent"
                    onClick={() => {
                      if (pasteLink.trim()) setCropImage(pasteLink.trim());
                    }}
                    className={styles.linkSearchIcon}
                    aria-label="Search by link"
                  >
                    <IconSearch size={20} color="black" />
                  </ActionIcon>
                </Tooltip>
              }
            />
          </Box>
        )}
      </Box>
    </MantineBox>
  );
}

export default ImageUploadZone;
